/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { getPaymentRequest } from 'services/billingServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

import useSpinner from 'hooks/useSpinner';

import CreatePaymentRequestV2 from './components/CreatePaymentRequestV2/CreatePaymentRequestV2';
import { useLocation } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { UserDetailsContext } from 'context/userDetailsContext';
import { handlePageNumberInPagination } from 'common/pagination';
import IntuitPaymentModal from 'modules/client/intuit/IntuitPaymentModal';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { usePaymentRequestListV2Query } from './hooks/queries/usePaymentRequestListV2Query';
import { useDeletePaymentRequestQuery } from './hooks/mutations/useDeletePaymentRequestQuery';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/paymentRequestsHelpersV2';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import PaymentRequestsFiltersV2 from './components/PaymentRequestsFiltersV2/PaymentRequestsFiltersV2';
import {
  idBody as idBodyHelper,
  caseBody as caseBodyHelper,
  viewBody as viewBodyHelper,
  statusBody as statusBodyHelper,
  totalBody as totalBodyHelper,
  dueBody as dueBodyHelper,
  dueTodayBody as dueTodayBodyHelper,
  nextDueDateBody as nextDueDateBodyHelper,
  sentBody as sentBodyHelper,
  actionBody as actionBodyHelper,
} from './helpers/paymentRequestsDataTableHelpersV2';
import { RecordPaymentV2 } from '../components/RecordPaymentV2/RecordPaymentV2';
import { RemindersV2 } from '../components/RemindersV2/RemindersV2';
import { SendNotificationMailV2 } from '../components/SendNotificationMailV2/SendNotificationMailV2';

export const PaymentRequestsV2 = (props) => {
  const { isFromCaseSpecificModule, getCaseDetails, getTimeEntrySummarty, isFromGlobalBilling, handlePaymentRequestTableRedirection } =
    props || {};
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [confirmSendMail, setConfirmSendMail] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const location = useLocation();
  const userContext = useContext(UserDetailsContext);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [pageLimit, setPageLimit] = useState(getPageLimit(props?.isLeadSpecific, isFromCaseSpecificModule, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);

  const { addToast } = useToast();
  const [spinner, showSpinner, hideSpinner] = useSpinner(true);

  // Delete payment request mutation
  const deletePaymentRequest = useDeletePaymentRequestQuery({
    onSuccess: () => {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DELETE_PAYMENT_REQUEST_SUCCESS);
      refetch();
      typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
      typeof getCaseDetails === 'function' && getCaseDetails();
    },
    onError: () => {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.DELETE_PAYMENT_REQUEST_FAILED);
    },
  });

  // Map DataTable field names to API sort_by field names
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // Return empty string when no sort field selected
    const fieldMap = {
      payment_request_number: 'payment_request_number',
      payment_request_date: 'payment_request_date',
      amount: 'amount',
      due_date: 'due_date',
      due_amount: 'due_amount',
      paid_amount: 'paid_amount',
      payment_request_status: 'payment_request_status',
      case_name: 'case_name',
      viewed_status: 'viewed_status',
      due_today: 'due_today',
      time: 'time',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // Return empty string when no sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Extract date range from calendar
  const dateRange = useMemo(() => {
    return pickExtractedDate(dateFilter, userContext?.userDetails?.timezone);
  }, [dateFilter, userContext?.userDetails?.timezone]);

  // Convert practice area selection to array of IDs
  const practiceAreaList = useMemo(() => {
    if (!selectedPractice || !Array.isArray(selectedPractice)) return [];
    return selectedPractice.map((item) => (typeof item === 'object' ? item.value : item));
  }, [selectedPractice]);

  // React Query hook for fetching payment requests
  const {
    paymentRequests,
    totalPages,
    currentPage: queryCurrentPage,
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = usePaymentRequestListV2Query({
    limit: pageLimit,
    page: currentPage,
    payment_request_status: '',
    case_id: props?.caseView ? props?.caseDetails?.case_id : '',
    search_key: searchKey,
    payment_date_lower: dateRange.payment_date_lower,
    payment_date_upper: dateRange.payment_date_upper,
    practice_area_list: practiceAreaList,
    sort_by: getCurrentSortField,
    sort_order: getCurrentSortOrder,
    enabled: true,
  });

  useEffect(() => {
    if (localStorage.getItem('redirectToRequestPayment')) {
      setShowModal(true);
      localStorage.removeItem('redirectToRequestPayment');
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentRequestSk = queryParams.get('payment_request_sk');

    if (paymentRequestSk && !selectedRequest) {
      const decodedPaymentRequestSk = decodeURIComponent(paymentRequestSk);

      getPaymentRequest(decodedPaymentRequestSk)
        .then((response) => {
          if (response?.data) {
            setSelectedRequest(response.data);
            setShowModal(true);
          }
        })
        .catch((error) => {
          console.error('Error fetching payment request:', error);
        });
    } else if (!paymentRequestSk && !selectedRequest && localStorage.getItem('viewPaymentRequest')) {
      let paymentRequestDetails = location?.state || getLocalStorageParseItem('stateData');

      if (paymentRequestDetails) {
        setSelectedRequest(paymentRequestDetails);
        setShowModal(true);
        removeLocalStorageItem('stateData');
        removeLocalStorageItem('viewPaymentRequest');
      }
    }
  }, [location, localStorage, window.localStorage]);

  useEffect(() => {
    if (isLoading || isFetching) {
      showSpinner();
    } else {
      hideSpinner();
    }
  }, [isLoading, isFetching]);

  const onCreateSuccess = () => {
    setShowModal(false);
    setShowRecordModal(false);
    refetch();
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
    typeof getCaseDetails === 'function' && getCaseDetails();
  };

  // Handler functions for action body
  const handleView = (rowData) => {
    setSelectedRequest({ ...rowData });
    setShowModal(true);
  };

  const handleSendMail = (rowData) => {
    setSelectedRequest({ ...rowData });
    setConfirmSendMail(true);
  };

  const handleRecordPayment = (rowData) => {
    setSelectedRequest({ ...rowData });
    setShowRecordModal(true);
  };

  const handleAcceptPayment = (rowData) => {
    setShowPaymentModal(rowData);
  };

  const handleReminder = (rowData) => {
    setSelectedRequest({ ...rowData });
    setShowReminder(true);
  };

  const handleDelete = (rowData) => {
    setSelectedRequest({ ...rowData });
    setConfirmDelete(true);
  };

  const deleteRequest = () => {
    if (!selectedRequest?.sk) return;

    setConfirmDelete(false);
    deletePaymentRequest.mutate({
      payment_request_sk: selectedRequest.sk,
      is_lead: props.isLeadSpecific ? props.isLeadSpecific : selectedRequest?.is_lead,
    });
  };

  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      setCurrentPage(1);
      refetch();
    }
  };

  const onChangeSearch = (e) => {
    let value = e.target.value;
    setSearchKey(value);
    if (!value) {
      setCurrentPage(1);
      refetch();
    }
  };

  const onChangeCalendar = (e) => {
    setDateFilter(e.value);
    setCurrentPage(1);
  };

  const onChangePractice = (e) => {
    setSelectedPractice([...e.value]);
    setCurrentPage(1);
  };

  const refreshCase = () => {
    setSearchKey('');
    setSelectedPractice(null);
    setDateFilter(null);
    setSelectedRequest();
    setCurrentPage(1);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    let path = getFilePathForPagination(props?.isLeadSpecific, isFromCaseSpecificModule, userContext);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
    setCurrentPage(1);
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
    setCurrentPage(1);
  };

  return (
    <div>
      {spinner}
      {showModal && (
        <CreatePaymentRequestV2
          show={showModal}
          onHide={(value) => {
            setShowModal(value);
            if (!value) {
              setSelectedRequest(null);
            }
          }}
          onCreateSuccess={onCreateSuccess}
          selectedRequest={selectedRequest}
          caseView={props.caseView}
          isLeadSpecific={props?.isLeadSpecific}
          caseDetails={props.caseDetails}
          billingSummary={props.billingSummary}
          getCaseDetails={getCaseDetails}
        />
      )}
      <ConfirmDialog
        header="Confirm Delete"
        message={' Do you want to delete this payment request?'}
        visible={confirmDelete}
        rejectClassName="p-button-secondary outline"
        icon="pi pi-exclamation-triangle"
        className="confirm-delete-popup"
        accept={() => {
          deleteRequest();
          typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
        }}
        reject={() => {
          setConfirmDelete(false);
          setSelectedRequest();
        }}
        onHide={() => {
          setConfirmDelete(false);
          setSelectedRequest();
        }}
      />
      {Boolean(showPaymentModal) && (
        <IntuitPaymentModal
          isFromFirm
          handleClose={() => setShowPaymentModal(null)}
          paymentDetails={showPaymentModal}
          loadData={onCreateSuccess}
        />
      )}

      {showRecordModal && (
        <RecordPaymentV2
          show={showRecordModal}
          onHide={() => {
            setShowRecordModal(false);
          }}
          selectedRequest={selectedRequest}
          onSuccess={onCreateSuccess}
          isLeadSpecific={props?.isLeadSpecific}
          caseDetails={props?.caseDetails}
        />
      )}

      {showReminder && (
        <RemindersV2
          show={showReminder}
          onHide={() => {
            setShowReminder(false);
          }}
          selectedRequest={selectedRequest}
          onSuccess={onCreateSuccess}
          isLeadSpecific={props?.isLeadSpecific}
        />
      )}

      <SendNotificationMailV2
        show={confirmSendMail}
        onHide={() => {
          setConfirmSendMail(false);
        }}
        selectedRequest={selectedRequest}
        isLeadSpecific={props?.isLeadSpecific}
      />
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center  mb-3">
            <Button
              label="Request Payment"
              icon="pi pi-plus"
              className="p-button p-button-primary ms-auto"
              onClick={() => {
                setSelectedRequest();
                setShowModal(true);
              }}
            ></Button>
          </div>
        </div>
      </div>

      <PaymentRequestsFiltersV2
        searchKey={searchKey}
        onChangeSearch={onChangeSearch}
        onSearchKeyDown={onSearchKeyDown}
        dateFilter={dateFilter}
        onChangeCalendar={onChangeCalendar}
        selectedPractice={selectedPractice}
        onChangePractice={onChangePractice}
        refreshData={refreshCase}
        isFromCaseSpecificModule={isFromCaseSpecificModule}
        isLeadSpecific={props?.isLeadSpecific}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={paymentRequests || []}
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          paginator={false}
          rows={pageLimit}
          storageKey="payment_request-list-v2"
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          columns={[
            {
              field: 'payment_request_number',
              header: 'ID',
              body: (rowData) => idBodyHelper({ rowData, onView: handleView }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'case_name',
              header: 'Case',
              body: (rowData) => caseBodyHelper({ rowData }),
              bodyClassName: 'ellipsis-text',
              hidden: !isFromGlobalBilling,
              sortable: true,
            },
            {
              field: 'viewed_status',
              header: 'Viewed',
              body: (rowData) => viewBodyHelper({ rowData, userContext }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'payment_request_status',
              header: 'Status',
              body: (rowData) => statusBodyHelper({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'amount',
              header: 'Total',
              body: (rowData) => totalBodyHelper({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'due_amount',
              header: 'Due',
              body: (rowData) => dueBodyHelper({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'due_today',
              header: 'Due Today',
              body: (rowData) => dueTodayBodyHelper({ rowData }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'due_date',
              header: 'Next Due Date',
              body: (rowData) => nextDueDateBodyHelper({ rowData, userContext }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'payment_request_date',
              header: 'Sent',
              body: (rowData) => sentBodyHelper({ rowData, userContext }),
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'action',
              header: 'Action',
              body: (rowData) =>
                actionBodyHelper({
                  rowData,
                  onSendMail: handleSendMail,
                  onRecordPayment: handleRecordPayment,
                  onAcceptPayment: handleAcceptPayment,
                  onReminder: handleReminder,
                  onDelete: handleDelete,
                  userContext,
                }),
              bodyClassName: 'text-nowrap',
              fixed: true,
              width: '130px',
              resizable: false,
            },
          ]}
        />

        <EllipsisPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          pageSize={pageLimit}
          totalRecords={totalCount}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />

        <Tooltip target={'#payReq'} position={'top'} showDelay={500} className="case-tooltip mb-3">
          Payment Request
        </Tooltip>
        <Tooltip target={'#recordPay'} position={'top'} showDelay={500} className="case-tooltip mb-3">
          Record Payment
        </Tooltip>
        <Tooltip target={'#reminder'} position={'top'} showDelay={500} className="case-tooltip mb-3">
          Reminder
        </Tooltip>
        <Tooltip target={'#delete'} position={'top'} showDelay={500} className="case-tooltip mb-3">
          Delete
        </Tooltip>
      </div>
    </div>
  );
};
